import axios, { AxiosError, AxiosResponse } from 'axios';
import config from './config';
import { GetStorageComponent } from './utils/session';
import { handleUnauthorized } from './utils/handleUnauthorized';
import { refreshAccessToken, tokenFromAuthHeader } from './utils/refreshAccessToken';
import { showError } from './components/AlertNotification';
import { ApiError } from './utils/ApiError';
import { reportError } from '@/utils/reportError';

const iaRequest = axios.create({
  baseURL: config.interviewAgentBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

iaRequest.interceptors.request.use(
  async config => {
    try {
      // const session = await fetchAuthSession();
      // const token = session.tokens?.accessToken?.toString();

      const token = GetStorageComponent('accessToken')?.toString();
      const idToken = GetStorageComponent('idToken')?.toString();

      if (config.headers && token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (config.headers && idToken) {
        config.headers['x-id-token'] = idToken;
      }
    } catch (error) {
      console.error('Error getting authentication token:', error);
      reportError(error, { tags: { layer: 'request-interceptor' } });
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

iaRequest.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status ?? null;
    const data = error.response?.data as Record<string, unknown> | undefined;
    let message = error.message;

    // On 401, attempt a silent access-token refresh (once per request) using the
    // HttpOnly refresh_token cookie, then replay the original request. Only if the
    // refresh fails do we fall through to the logout/redirect path below.
    const originalRequest = error.config;
    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const newAccessToken = await refreshAccessToken(tokenFromAuthHeader(originalRequest.headers?.Authorization));
      if (newAccessToken) {
        // Re-running through iaRequest replays the request interceptor, which
        // re-reads the freshly stored accessToken/idToken from storage.
        return iaRequest(originalRequest);
      }
    }

    if (error.response) {
      message = (data?.message as string) ?? (data?.error as string) ?? `Request failed with status ${status}`;
      console.error(`HTTP ${status} - ${error.response.statusText}`, data);
    } else if (error.request) {
      message = 'No response from server. Please check your connection.';
      console.error('No response received:', error.request);
    } else {
      console.error('Axios error:', error.message);
    }

    if (status === 401) {
      handleUnauthorized();
    }

    if (!error.config?.suppressError) {
      showError(message);
    }

    // Reported to Sentry once, by QueryCache/MutationCache onError in QueryProvider,
    // with the ApiError shape so it groups correctly instead of as a plain object.
    return Promise.reject(new ApiError(message, status, data ?? null, error.config));
  },
);

export default iaRequest;
